import {
	Stack,
	Text,
	Box,
	Button,
	Inline,
	Icons,
	CheckboxInput,
	Table,
	Spinner,
	Heading,
	Dropdown,
	Component,
	DeferRender,
	joinAttributes,
	RelativeTime,
	Stars,
	Time,
	Money,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	utcTimestampToLocalDate,
	utcTimestampToLocalDateTimeString,
	getDiff,
	localOrUtcTimestampToLocalDate,
	formatDate,
	subtractUnit,
	dateToUTCString,
	parseDate,
	dateToQuery,
} from "@sembark-travel/datetime-utils"
import { isNumeric, withOrdinalSuffix } from "@sembark-travel/number-utils"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import React, { Fragment, useMemo, useReducer, useRef, useState } from "react"
import useSWR from "swr"
import { $PropertyType } from "utility-types"
import { GeneratedVoucherAsset, SendHotelVoucher } from "../HotelVouchers"
import {
	IBooking,
	ITripHotelBooking,
	EditHotelBookingDetailsInDialog,
	HotelBookingDetailsForm,
	HotelBookingCurrentStage,
	UpdateBookingTagInDialog,
	GenerateVoucher,
	TSelfBookedHotels,
	getRoomStatusOnDate,
} from "./../HotelBookings"
import { TTripDestination } from "../TripDestinations"
import {
	HotelBookingItemInDialog,
	SendBookingInPopover,
	ShareHotelBooking,
} from "../HotelBookings/Item"
import { moneyParseByDecimal } from "@sembark-travel/money"
import { PhoneNumber } from "../Contacts"
import { SelfBookForm, SelfBookFormValues } from "../HotelPrices/CalculatePrice"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { Markdown } from "@sembark-travel/ui-markdown"
import { getHotelStayDatesWithNotes, THotelNote } from "../HotelNotes"
import { getOrderedUniqueHotelNotes } from "../HotelPrices"
import { getGlobalNotesForHotelDestination, IHotel } from "../Hotels"
import { IMealPlan } from "../MealPlans"
import { IRoomType } from "../RoomTypes"
import { NavLink, queryToSearch } from "@sembark-travel/ui-router"
import { generatePath } from "../router-utils"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { EntryTypeIcon } from "../shared/EntryTypeConfig"

function XHR(xhr: XHRInstance) {
	return {
		async show(tripId: string): Promise<ITripHotelBooking> {
			return xhr.get(`/trip-hotel-bookings/${tripId}`, {}).then((resp) => {
				if ((resp.data.meta.general_hotel_notes?.length || 0) > 0) {
					const data = resp.data.data
					const globalNotes = resp.data.meta.general_hotel_notes
					return {
						...data,
						bookings: data.bookings.map(
							(booking: ITripHotelBooking["bookings"][0]) => ({
								...booking,
								hotel: {
									...booking.hotel,
									global_notes: getGlobalNotesForHotelDestination({
										generalNotes: globalNotes,
										hotelDestination: booking.hotel.trip_destinations,
									}),
								},
							})
						),
					}
				} else {
					return resp.data.data
				}
			})
		},
	}
}

const VOUCHER_POLL_INTERVAL = 3000
// ~2 mins; BE voucher jobs may fail without telling us
const VOUCHER_MAX_POLLS = 40

function isVoucherReady(booking: ITripHotelBooking["bookings"][number]) {
	return Boolean(booking.voucher?.file_url && booking.voucher?.sent_at)
}

interface IHotelBookings {
	tripId: string
	readOnly?: boolean
}
interface GroupedHotel {
	hotel: IHotel
	dates: string[]
	meal_plans: IMealPlan[]
	room_types: IRoomType[]
	notes: (THotelNote & { dates: Date[] })[]
}
export default function HotelBookings({ tripId, readOnly }: IHotelBookings) {
	const xhr = useXHR()
	const [awaitingVoucher, setAwaitingVoucherState] = useState<
		Record<number, number>
	>({})
	const awaitingVoucherRef = useRef(awaitingVoucher)
	function setAwaitingVoucher(next: Record<number, number>) {
		awaitingVoucherRef.current = next
		setAwaitingVoucherState(next)
	}
	const {
		data,
		error,
		mutate: revalidate,
		isValidating,
	} = useSWR(
		tripId ? `trip-hotel-bookings/${tripId}` : null,
		() => (tripId ? XHR(xhr).show(tripId) : undefined),
		{
			refreshInterval:
				Object.keys(awaitingVoucher).length > 0 ? VOUCHER_POLL_INTERVAL : 0,
			onSuccess(latest) {
				const awaiting = awaitingVoucherRef.current
				if (!latest || Object.keys(awaiting).length === 0) return
				const next: Record<number, number> = {}
				let hasTimedOut = false
				for (const [id, polls] of Object.entries(awaiting)) {
					const booking = latest.bookings.find((b) => b.id === Number(id))
					if (!booking || isVoucherReady(booking)) continue
					if (polls >= VOUCHER_MAX_POLLS) {
						hasTimedOut = true
						continue
					}
					next[Number(id)] = polls + 1
				}
				setAwaitingVoucher(next)
				if (hasTimedOut) {
					showSnackbar(
						"Voucher is taking longer than expected. Please check again later."
					)
				}
			},
		}
	)
	const { hasPermission } = useCheckPermissions()
	const [filters, setFilters] = useReducer<
		React.Reducer<
			{ showDropped: boolean; showChanged: boolean },
			{ showDropped?: boolean; showChanged?: boolean }
		>
	>((state, action) => ({ ...state, ...action }), {
		showDropped: true,
		showChanged: true,
	})

	const changedBookings = useMemo(() => {
		if (!data) return []
		return data.bookings.filter((b) => b.booking_stage.state === "changed")
	}, [data])
	const droppedBookings = useMemo(() => {
		if (!data) return []
		return data.bookings.filter((b) => b.booking_stage.state === "dropped")
	}, [data])
	const tripBookings = useMemo(() => {
		if (!data) return undefined
		const { showChanged, showDropped } = filters
		return {
			...data,
			bookings: data.bookings.filter((b) => {
				if (!showChanged && b.booking_stage.state === "changed") return false
				if (!showDropped && b.booking_stage.state === "dropped") return false
				return true
			}),
		}
	}, [filters, data])
	const isFetching: boolean = !tripBookings && !error
	const hotelNotes = useMemo(() => {
		if (!tripBookings?.bookings.length) return []

		const hotelMap: Record<string, GroupedHotel> = {}
		const uniqueGlobalNotes: (THotelNote & { dates: Date[] })[] = []

		for (const booking of tripBookings.bookings) {
			const hotelId = booking.hotel.id

			// Initialize hotel group if missing
			if (!hotelMap[hotelId]) {
				hotelMap[hotelId] = {
					hotel: booking.hotel,
					dates: [],
					meal_plans: [],
					room_types: [],
					notes: [],
				}
			}

			const group = hotelMap[hotelId]

			for (const detail of booking.details ?? []) {
				// Add unique meal_plan
				if (
					detail.meal_plan &&
					!group.meal_plans.some((mp) => mp.id === detail.meal_plan.id)
				) {
					group.meal_plans.push(detail.meal_plan)
				}

				// Add unique room_type
				if (
					detail.room_type &&
					!group.room_types.some((rt) => rt.id === detail.room_type.id)
				) {
					group.room_types.push(detail.room_type)
				}

				const parsedDate = parseDate(detail.date)
				const notes = detail.notes?.filter((n) => n.show_in_voucher) || []

				// Split into hotel-specific and general notes
				const hotelNotes = notes.filter((n) => n.hotel_id !== null)
				const generalNotes = notes.filter((n) => n.hotel_id === null)

				if (hotelNotes.length) {
					getHotelStayDatesWithNotes(group.notes, hotelNotes, [parsedDate])
				}

				if (generalNotes.length) {
					getHotelStayDatesWithNotes(uniqueGlobalNotes, generalNotes, [
						parsedDate,
					])
				}
			}
		}

		// Build output
		const notes: Record<string, Record<string, string[]>> = {}
		const hotelGroups = Object.values(hotelMap)

		// Add general notes section (if any)
		const generalHotelNotes = getOrderedUniqueHotelNotes({
			attachedNotes: uniqueGlobalNotes,
			mealIds: hotelGroups.flatMap((h) => h.meal_plans?.map((m) => m.id) ?? []),
			roomTypeIds: [],
			tripDestinationIds:
				tripBookings.destinations?.map((destination) => destination.id) ?? [],
		})

		if (Object.keys(generalHotelNotes).length > 0) {
			notes["Notes applicable to all hotels"] = generalHotelNotes
		}

		// Add notes for each hotel
		for (const hotel of hotelGroups) {
			const hotelSpecificNotes = getOrderedUniqueHotelNotes({
				attachedNotes: hotel.notes.filter(
					(n) => n.show_in_voucher && n.hotel_id !== null
				),
				mealIds: hotel.meal_plans?.map((m) => m.id) ?? [],
				roomTypeIds: hotel.room_types?.map((r) => r.id) ?? [],
			})

			if (Object.keys(hotelSpecificNotes).length > 0) {
				notes[hotel.hotel.name] = hotelSpecificNotes
			}
		}

		const result = Object.entries(notes).map(([hotel, notesObj]) => {
			const notes = Object.entries(notesObj).map(([key, descriptions]) => ({
				key,
				descriptions: descriptions,
			}))

			return { hotel, notes }
		})

		return result
	}, [tripBookings])
	if (isFetching) return <Spinner alignCenter padding="4" />
	if (!tripBookings) {
		return null
	}
	const {
		bookings = [],
		self_bookings = [],
		can_modify,
		is_shared,
		can_view_prices,
		destinations,
		is_locked,
	} = tripBookings
	const tripStartDate = localOrUtcTimestampToLocalDate(
		tripBookings.start_date_local,
		tripBookings.start_date
	)
	const tripEndDate = localOrUtcTimestampToLocalDate(
		tripBookings.end_date_local,
		tripBookings.end_date
	)
	const can_Edit = Boolean(can_modify && !readOnly && !is_locked)

	return (
		<Stack gap="4" cursor={isValidating ? "wait" : undefined}>
			<Inline gap="4" collapseBelow="sm" justifyContent="between">
				<Heading>Hotel Bookings</Heading>
				<Box display="flex" justifyContent="end" gap="4">
					{droppedBookings.length ? (
						<Box as="label" marginLeft="4" title="Show/Hide Dropped Hotels">
							<CheckboxInput
								checked={filters.showDropped}
								onChange={() =>
									setFilters({ showDropped: !filters.showDropped })
								}
							/>{" "}
							{droppedBookings.length} Dropped
						</Box>
					) : null}
					{changedBookings.length ? (
						<Box as="label" marginLeft="4" title="Show/Hide Changed Hotels">
							<CheckboxInput
								checked={filters.showChanged}
								onChange={() =>
									setFilters({ showChanged: !filters.showChanged })
								}
							/>{" "}
							{changedBookings.length} Changed
						</Box>
					) : null}

					<Component initialState={false}>
						{({ state, setState }) => (
							<>
								<Dropdown alignRight marginLeft="2">
									<Dropdown.ToggleButton level="tertiary">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu style={{ zIndex: 100 }}>
										{hasPermission(PERMISSIONS.VIEW_HOTEL_BOOKINGS) && (
											<NavLink
												to={`${generatePath("/hotel-bookings/calendar")}${queryToSearch(
													{
														date: dateToQuery(tripStartDate),
													}
												)}`}
											>
												<Icons.Calendar /> View on Calendar
											</NavLink>
										)}
										{!can_Edit ? null : (
											<>
												{
													//Disabled for Hotel CRM
													/*<Dropdown.MenuItem onClick={() => setState(true)}>
													<Icons.Plus />
													Add More Hotels/Services
												  </Dropdown.MenuItem>*/
												}
												<Component initialState={false}>
													{({ state: isUpdating, setState: setIsUpdating }) => (
														<Dropdown.MenuItem
															onClick={(e: React.MouseEvent) => {
																e.preventDefault()
																if (isUpdating) {
																	return false
																}
																if (
																	window.confirm(
																		"Hotel bookings will be updated using the latest Quote from this Trip.\n\nAre you sure you want to continue?"
																	)
																) {
																	setIsUpdating(true)
																	xhr
																		.post(
																			`/trip-hotel-bookings/${tripBookings.id}/sync-with-latest-quote`
																		)
																		.then(() => {
																			showSnackbar(
																				"Hotel bookings have been synced with Latest Quote"
																			)
																		})
																		.catch((error) => {
																			const e = error as Error
																			alert(
																				e.message ||
																					"Something went wrong. Please try after sometime."
																			)
																		})
																		.then(() => {
																			// always revalidate
																			revalidate()
																			setIsUpdating(false)
																		})
																}
															}}
														>
															<Icons.Refresh spin={isUpdating} /> Sync Bookings
															with Quote
														</Dropdown.MenuItem>
													)}
												</Component>
											</>
										)}
										{
											//Disabled for Hotel CRM
											/*<AddSelfBookingDialog
											tripId={tripBookings.id}
											startDate={tripStartDate}
											endDate={tripEndDate}
											tripDestinations={destinations}
											onSuccess={() => revalidate()}
										>
											{({ onCreate }) => (
												<Dropdown.MenuItem onClick={onCreate}>
													<Icons.Plus title="self booked accommodation" /> Add
													Self Booked Accommodation
												</Dropdown.MenuItem>
											)}
										</AddSelfBookingDialog>*/
										}
									</Dropdown.Menu>
								</Dropdown>
								<Dialog
									open={state}
									onClose={() => setState(false)}
									fitContainer
									title="Add More Hotel/Services Bookings"
								>
									<Dialog.Body>
										<DeferRender>
											<AddMoreHotels
												tripId={tripBookings.id}
												tripDestinations={destinations}
												startDate={tripStartDate}
												endDate={tripEndDate}
												onCancel={() => setState(false)}
												children={tripBookings.children}
												noOfAdults={tripBookings.no_of_adults}
												onSuccess={() => {
													setState(false)
													setTimeout(() => {
														revalidate()
													}, 1000)
												}}
											/>
										</DeferRender>
									</Dialog.Body>
								</Dialog>
							</>
						)}
					</Component>
				</Box>
			</Inline>
			{!bookings.length && !self_bookings.length ? (
				<Text>No hotel bookings for this trip</Text>
			) : (
				<Stack gap="4">
					{bookings.length ? (
						<Table className="excel-style-table" responsive>
							<thead>
								<tr>
									<th>Hotel</th>
									<th>Stay and Services</th>
									<th>Status</th>
									<th>Tag/Comments</th>
									{can_view_prices ? (
										<th style={{ textAlign: "right" }}>Price</th>
									) : null}
								</tr>
							</thead>
							<tbody>
								{bookings.map((booking, idx) => {
									const {
										id,
										hotel,
										booking_stage,
										booked,
										checkin: bookingStartDate,
										checkin_local: bookingStartDateLocal,
										no_of_nights,
										voucher,
										booked_at,
										booking_confirmation_id,
										hotel_contact,
										supplier_contact,
										supplier,
										currency,
										booked_price,
										payments_amount,
										paid_payments_amount,
										refunding_payments_amount,
										paid_refunding_payments_amount,
										cancellation_charges,
									} = booking
									const isAwaitingVoucher = id in awaitingVoucher

									const checkin = localOrUtcTimestampToLocalDate(
										bookingStartDateLocal,
										bookingStartDate
									)
									const stay_nights = [
										getDiff(checkin, tripStartDate, "days") + 1,
									]
									for (let i = 1; i < no_of_nights; i++) {
										stay_nights.push(stay_nights[i - 1] + 1)
									}

									return (
										<Box as="tr" key={id}>
											<Box as="td" verticalAlign="top">
												<Box
													display="flex"
													alignItems="center"
													justifyContent="between"
												>
													<Box>
														<Box fontWeight="semibold">
															<HotelBookingItemInDialog
																hotelBookingId={booking.id}
																readOnly={!can_Edit}
															>
																{({ showDetails }) => (
																	<Text<"a">
																		as="a"
																		href="#hotel-booking-details"
																		fontWeight="semibold"
																		color="accent"
																		onClick={(e) => {
																			e.preventDefault()
																			showDetails()
																		}}
																	>
																		<Inline gap="1">
																			<Text>{hotel?.name}</Text>
																			{hotel ? (
																				<Box>
																					<EntryTypeIcon
																						entryType={hotel?.entry_type}
																						createdBy={hotel?.created_by?.name}
																					/>
																				</Box>
																			) : null}
																		</Inline>
																	</Text>
																)}
															</HotelBookingItemInDialog>
														</Box>
														<Box fontSize="sm">
															{joinAttributes(
																hotel?.location?.short_name,
																hotel.stars ? (
																	<Stars stars={hotel.stars} />
																) : null
															)}
														</Box>
													</Box>
													{can_Edit ? (
														<Box marginLeft="1">
															<Inline gap="4">
																<EditHotelBookingDetailsInDialog
																	tripDestinations={destinations}
																	booking={booking}
																	startDate={tripStartDate}
																	endDate={tripEndDate}
																	onSuccess={() => revalidate()}
																	minimal
																/>
																<ShareHotelBooking
																	tripDestinations={destinations}
																	booking={booking}
																	tripStartDate={tripStartDate}
																	onChange={() => revalidate()}
																	tripEndDate={tripEndDate}
																	minimal
																/>
															</Inline>
														</Box>
													) : null}
												</Box>
												{booked && booking_confirmation_id ? (
													<Box
														title="Booking Confirmation ID"
														fontSize="sm"
														marginTop="2"
													>
														CNF: <b>{booking_confirmation_id}</b>
													</Box>
												) : null}
												{booked && hotel_contact ? (
													<Box fontSize="sm" marginTop="1">
														<Text
															as="span"
															title="Booking Confirmation By Contact"
														>
															<Icons.ArrowLeft rotate="180" size="3" />{" "}
															{hotel_contact.name}{" "}
														</Text>
														<PhoneNumber
															value={hotel_contact.phone_numbers}
															iconOnly
														/>
													</Box>
												) : null}
											</Box>
											<Box as="td">
												<Box>
													<Box>
														<Time value={checkin} format="Do MMMM" />
														<Box display="inline" marginLeft="1">
															-{" "}
															{stay_nights
																.map((s) => withOrdinalSuffix(s))
																.join(",")}{" "}
															N
														</Box>
													</Box>
													{booking.details?.length ? (
														<Box marginTop="1" paddingTop="1" fontSize="xs">
															{booking.details.map((bookingDetail, i) => {
																const {
																	date,
																	date_local,
																	meal_plan,
																	room_type,
																	no_of_rooms,
																	comments,
																	adults_with_extra_bed,
																	children_with_extra_bed,
																	children_without_extra_bed,
																} = bookingDetail
																const status = getRoomStatusOnDate(
																	{
																		black_out_date_intervals:
																			bookingDetail.room_type
																				.black_out_date_intervals,
																	},
																	parseDate(bookingDetail.date)
																)
																const night =
																	getDiff(
																		localOrUtcTimestampToLocalDate(
																			date_local,
																			date
																		),
																		checkin,
																		"days"
																	) + 1
																return (
																	<Box
																		key={`${bookingDetail.id}-${id}-${i}`}
																		title={`${withOrdinalSuffix(
																			night
																		)} Night Services`}
																		display="flex"
																		alignItems="center"
																		gap="1"
																	>
																		{status === "stop_sale" && (
																			<Icons.Ban color="danger" />
																		)}
																		{status === "blackout" && (
																			<Icons.HighDemand color="warning" />
																		)}
																		{stay_nights.length > 1 ? (
																			<Box display="inline">
																				{night}N{" - "}
																			</Box>
																		) : null}
																		<Box display="inline">
																			{joinAttributes(
																				meal_plan.name,
																				`${no_of_rooms} ${room_type.name}`,
																				[
																					!!adults_with_extra_bed,
																					`${adults_with_extra_bed} AWEB`,
																				],
																				[
																					!!children_with_extra_bed,
																					`${children_with_extra_bed} CWEB`,
																				],
																				[
																					!!children_without_extra_bed,
																					`${children_without_extra_bed} CNB`,
																				]
																			)}
																		</Box>
																		{!is_shared && comments ? (
																			<Box maxWidth="sm">
																				<blockquote>{comments}</blockquote>
																			</Box>
																		) : null}
																	</Box>
																)
															})}
														</Box>
													) : (
														<Box fontSize="xs" color="muted" marginTop="1">
															No Room Services
														</Box>
													)}
													{booking.extras?.length || booking.addons?.length ? (
														<Box marginTop="2">
															<Box fontWeight="semibold">Extra Services</Box>
															<Box fontSize="xs" marginTop="1">
																{(booking.extras || []).map((extra, i) => {
																	const { date, service, comments } = extra
																	return (
																		<Box key={`${extra.id}-${id}-${i}`}>
																			{stay_nights.length > 1 && date ? (
																				<Box display="inline">
																					{getDiff(
																						date,
																						booking.checkin,
																						"days"
																					) + 1}{" "}
																					N{" - "}
																				</Box>
																			) : null}
																			<Box display="inline">
																				{joinAttributes(service.name)}
																			</Box>
																			{!is_shared && comments ? (
																				<blockquote>{comments}</blockquote>
																			) : null}
																		</Box>
																	)
																})}
																{(booking.addons || []).map((addon, i) => {
																	const {
																		date,
																		date_local,
																		ota_addon,
																		quantity,
																		comments,
																	} = addon
																	return (
																		<Box key={`addon-${addon.id}-${id}-${i}`}>
																			{stay_nights.length > 1 ? (
																				<Box display="inline">
																					{getDiff(
																						localOrUtcTimestampToLocalDate(
																							date_local,
																							date
																						),
																						checkin,
																						"days"
																					) + 1}{" "}
																					N{" - "}
																				</Box>
																			) : null}
																			<Box display="inline">
																				{ota_addon.name} × {quantity}
																			</Box>
																			{!is_shared && comments ? (
																				<blockquote>{comments}</blockquote>
																			) : null}
																		</Box>
																	)
																})}
															</Box>
														</Box>
													) : null}
												</Box>
											</Box>
											<Box as="td" verticalAlign="top">
												<Box
													title={utcTimestampToLocalDateTimeString(
														booking_stage.created_at
													)}
												>
													<HotelBookingCurrentStage
														tripDestinations={destinations}
														booking={booking}
														tripStartDate={tripStartDate}
														tripEndDate={tripEndDate}
														onChangeSuccess={() => revalidate()}
														minimal
														readOnly={!can_Edit}
													/>
													<SendBookingInPopover
														booking={booking}
														onSuccess={() => revalidate()}
														onSent={() =>
															setAwaitingVoucher({
																...awaitingVoucherRef.current,
																[booking.id]: 0,
															})
														}
													/>
													{booking_stage.created_by &&
													booking_stage.created_at ? (
														<Box fontSize="sm" color="muted" marginTop="1">
															{joinAttributes(
																`by ${booking_stage.created_by.name}`,
																<RelativeTime
																	value={utcTimestampToLocalDate(
																		booking_stage.created_at
																	)}
																/>
															)}
														</Box>
													) : null}
													{booked ? (
														<>
															{!voucher ? (
																<>
																	{isAwaitingVoucher ? (
																		<Inline
																			as="span"
																			gap="1"
																			alignItems="center"
																			fontSize="sm"
																			color="muted"
																			marginTop="2"
																		>
																			<Spinner /> Generating voucher...
																		</Inline>
																	) : can_Edit ? (
																		<GenerateVoucher
																			hotel={hotel}
																			booking={booking}
																			isBooked={!!booked_at}
																			bookingConfirmationId={
																				booking_confirmation_id
																			}
																			onGenerate={() => revalidate()}
																			minimal
																		>
																			<Box as="span" color="warning">
																				<Icons.Document /> Generate Voucher
																			</Box>
																		</GenerateVoucher>
																	) : null}
																</>
															) : (
																<Inline marginTop="2" alignItems="center">
																	{joinAttributes(
																		<GeneratedVoucherAsset
																			id={voucher.id}
																			fileUrl={voucher.file_url}
																			isGenerating={voucher.is_generating}
																			onChange={() => revalidate()}
																			fontSize="sm"
																		/>,
																		!can_Edit ? null : voucher.sent_at ? (
																			<Box
																				title={`Voucher sent on ${utcTimestampToLocalDateTimeString(
																					voucher.sent_at
																				)} by ${voucher.sent_by?.name}`}
																				display="inline"
																				fontSize="sm"
																			>
																				<Icons.Ok color="success" /> Voucher
																				Sent
																			</Box>
																		) : isAwaitingVoucher ? (
																			<Inline
																				as="span"
																				gap="1"
																				alignItems="center"
																				fontSize="sm"
																				color="muted"
																			>
																				<Spinner /> Sending voucher...
																			</Inline>
																		) : voucher.is_generating ||
																		  !voucher.file_url ? null : (
																			<SendHotelVoucher
																				voucherId={voucher.id}
																				onSuccess={() => revalidate()}
																			>
																				<Text as="span">Send Voucher</Text>
																			</SendHotelVoucher>
																		),
																		can_Edit && !isAwaitingVoucher ? (
																			<GenerateVoucher
																				hotel={hotel}
																				booking={booking}
																				isBooked={!!booked_at}
																				bookingConfirmationId={
																					booking_confirmation_id
																				}
																				onGenerate={() => revalidate()}
																				isShared={is_shared}
																				minimal
																			>
																				<Icons.Refresh opacity="50" />
																			</GenerateVoucher>
																		) : null
																	)}
																</Inline>
															)}
														</>
													) : null}
												</Box>
												{booked && supplier && supplier_contact ? (
													<Box
														fontSize="sm"
														marginTop="1"
														fontWeight="semibold"
													>
														<Text
															as="span"
															title="Booking Confirmation By Contact"
														>
															<Icons.ArrowLeft rotate="180" size="3" />{" "}
															{supplier_contact.name}{" "}
														</Text>
														<PhoneNumber
															value={supplier_contact.phone_numbers}
															iconOnly
														/>
													</Box>
												) : null}
											</Box>
											<Box as="td" verticalAlign="top">
												<Inline gap="4">
													<Box>{booking_stage.tag || null}</Box>
													{can_Edit ? (
														<UpdateBookingTagInDialog
															booking={booking}
															onSuccess={() => revalidate()}
														>
															{({ onEdit }) => (
																<Button onClick={() => onEdit()} inline>
																	<Icons.Pencil />
																</Button>
															)}
														</UpdateBookingTagInDialog>
													) : null}
												</Inline>
												{booking_stage.comments ? (
													<Box
														fontSize="sm"
														style={{ maxWidth: "200px" }}
														verticalAlign="top"
														textOverflow="truncate"
														color="muted"
														marginTop="1"
													>
														<Box title={booking_stage.comments}>
															{booking_stage.comments}
														</Box>
													</Box>
												) : null}
											</Box>
											{can_view_prices ? (
												<Box
													as="td"
													verticalAlign="top"
													style={{ textAlign: "right" }}
												>
													{isNumeric(booked_price) ? (
														<Inline
															fontWeight="semibold"
															alignItems="center"
															justifyContent="end"
															gap="1"
														>
															{refunding_payments_amount === null ? (
																// If not in refund mode, show Green Check when
																// - Booking is in Booked/Changed stage
																// - or, when booking with no paid instalments is dropped without cancellation charges
																// AND, when all payments are paid.
																// In case payments_amount is null, paid_payments_amount must also be null.
																[
																	"changed",
																	"booked",
																	"dropped",
																	"trip_dropped",
																].includes(booking.booking_stage.state) &&
																paid_payments_amount === payments_amount ? (
																	<Box display="inlineBlock" marginLeft="1">
																		<Icons.OkCircleSolid color="success" />
																	</Box>
																) : null
															) : paid_refunding_payments_amount ===
															  refunding_payments_amount ? (
																// If in refund mode, show Green Check when
																// - Refund is complete
																<Box display="inlineBlock" marginLeft="1">
																	<Icons.OkCircleSolid color="success" />
																</Box>
															) : null}
															<Box>Booking:</Box>
															<Money
																money={moneyParseByDecimal(
																	booked_price,
																	currency
																)}
																showCurrency
															/>
														</Inline>
													) : null}
													{isNumeric(payments_amount) ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															{isNumeric(refunding_payments_amount) &&
															isNumeric(cancellation_charges) ? (
																<Box>
																	Net Payable:{" "}
																	<Money
																		amount={cancellation_charges}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : isNumeric(booked_price) &&
															  Number(payments_amount || 0) ===
																	Number(booked_price || 0) ? null : (
																<Box>
																	Net Payable:{" "}
																	<Money
																		amount={payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															)}
														</Box>
													) : //show net payaable as 0 if booking_status is either changed, dropped or trip dropped
													booking.booking_stage.state === "changed" ||
													  booking.booking_stage.state === "dropped" ||
													  booking.booking_stage.state === "trip_dropped" ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															Net Payable:{" "}
															<Money
																amount={0}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : null}

													{isNumeric(paid_payments_amount) ? (
														<>
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="success"
															>
																Amount Paid:{" "}
																<Money
																	amount={paid_payments_amount}
																	currency={currency}
																	showCurrency
																/>
															</Box>
															{isNumeric(payments_amount) ? (
																Number(payments_amount || 0) >
																Number(paid_payments_amount || 0) ? (
																	<Box
																		fontWeight="semibold"
																		fontSize="sm"
																		color="muted"
																	>
																		Due Payable:{" "}
																		<Money
																			amount={
																				payments_amount - paid_payments_amount
																			}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																) : null
															) : null}
														</>
													) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
													isNumeric(payments_amount) &&
													  Number(payments_amount || 0) > 0 ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															Amount Paid:{" "}
															<Money
																amount={0}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : null}
													{isNumeric(refunding_payments_amount) ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															Refund:{" "}
															<Money
																money={moneyParseByDecimal(
																	Number(paid_refunding_payments_amount || 0),
																	currency
																)}
																showCurrency
															/>
															{" / "}
															<Money
																amount={refunding_payments_amount}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : null}
												</Box>
											) : null}
										</Box>
									)
								})}
							</tbody>
						</Table>
					) : null}

					{hotelNotes.length ? (
						<Component initialState={false}>
							{({ state, setState }) => {
								return (
									<Stack
										gap="1"
										borderWidth="1"
										rounded="md"
										bgColor={"white"}
										overflow="hidden"
									>
										{state ? (
											<Stack gap="2">
												<Box>
													{hotelNotes.map(({ hotel, notes }, i) => (
														<Stack key={i} gap={"2"}>
															<Box
																padding={"1"}
																backgroundColor={"subtle"}
																borderBottomWidth="1"
																borderColor={"emphasis"}
															>
																<Text fontWeight="semibold" fontSize={"md"}>
																	{hotel}
																</Text>
															</Box>
															{notes.map(({ key, descriptions }, noteId) => (
																<Box paddingLeft={"2"} key={noteId}>
																	<Text fontWeight="semibold" color="accent">
																		{key}:
																	</Text>
																	{descriptions.map((description, index) => (
																		<Markdown key={index}>
																			{description}
																		</Markdown>
																	))}
																</Box>
															))}
														</Stack>
													))}
												</Box>
											</Stack>
										) : null}
										<Box padding="2">
											<Button
												title="notes"
												inline
												onClick={() => {
													setState(!state)
												}}
											>
												<Text as="span" fontWeight="semibold" color="primary">
													{state ? "Hide Notes" : "Show Notes"}
												</Text>
												<Text as="span">
													<Icons.ChevronDown
														rotate={state ? "180" : undefined}
														transition="1"
													/>
												</Text>
											</Button>
										</Box>
									</Stack>
								)
							}}
						</Component>
					) : null}

					{self_bookings.length ? (
						<Stack gap={"4"}>
							<Text fontSize={"md"} fontWeight="semibold">
								Self Booked Accommodation
							</Text>
							<Table className="excel-style-table" responsive>
								<thead>
									<tr>
										<th>Accommodation</th>
										<th>Stay</th>
										<th>Remarks</th>
									</tr>
								</thead>
								<tbody>
									{self_bookings.map((booking) => {
										const { id, hotel, checkin_dates, name, remarks } = booking

										return (
											<Box as="tr" key={id}>
												<Box as="td" verticalAlign="top">
													<Box>
														<Box fontWeight="semibold">{name}</Box>
														{hotel ? (
															<Box fontSize="sm">
																{joinAttributes(
																	hotel?.location?.short_name,
																	hotel.stars ? (
																		<Stars stars={hotel.stars} />
																	) : null
																)}
															</Box>
														) : null}
													</Box>
												</Box>
												<Box as="td" style={{ maxWidth: "8rem" }}>
													<Box display="inline" marginLeft="1">
														{checkin_dates.map((d, i) => (
															<Fragment key={i}>
																<Time value={d} format="Do MMM" /> (
																{withOrdinalSuffix(
																	getDiff(d, tripStartDate, "days") + 1
																)}
																){i !== checkin_dates.length - 1 ? " , " : ""}
															</Fragment>
														))}
													</Box>
												</Box>
												<Box as="td" maxWidth={"xs"}>
													<Inline
														gap={"4"}
														alignItems={"baseline"}
														justifyContent={"between"}
													>
														<Text>{remarks}</Text>
														<Inline alignItems={"center"} gap={"1"}>
															<EditSelfBookedDialog
																booking={booking}
																startDate={tripStartDate}
																endDate={tripEndDate}
																tripDestinations={destinations}
																onSuccess={() => revalidate()}
															>
																{({ onEdit }) => (
																	<Button
																		level="tertiary"
																		inline
																		onClick={onEdit}
																	>
																		<Icons.Pencil title="Edit self booked accommodation" />
																	</Button>
																)}
															</EditSelfBookedDialog>
															<Dropdown alignRight>
																<Dropdown.ToggleButton
																	level="tertiary"
																	size="sm"
																>
																	<Icons.DotsVertical />
																</Dropdown.ToggleButton>
																<Dropdown.Menu>
																	<Dropdown.MenuItem
																		onClick={async () => {
																			if (
																				window.confirm(
																					"Are you sure you want to delete self booked accommodation ?"
																				)
																			) {
																				try {
																					const resp = await xhr.delete(
																						`self-booked-hotel/${id}`
																					)
																					revalidate()
																					showSnackbar(
																						resp.data.message ||
																							`${name} self booked accommodation deleted successfully`
																					)
																				} catch (e) {
																					const error = e as Error
																					window.alert(error.message)
																				}
																			}
																		}}
																	>
																		<Icons.Trash title="delete accommodation" />{" "}
																		Delete
																	</Dropdown.MenuItem>
																</Dropdown.Menu>
															</Dropdown>
														</Inline>
													</Inline>
												</Box>
											</Box>
										)
									})}
								</tbody>
							</Table>
						</Stack>
					) : null}
				</Stack>
			)}
		</Stack>
	)
}

function AddMoreHotels({
	startDate,
	endDate,
	children,
	tripId,
	noOfAdults,
	onCancel,
	onSuccess,
	tripDestinations,
}: {
	startDate: Date
	endDate: Date
	tripId: number
	children: $PropertyType<IBooking, "children">
	noOfAdults: number
	onCancel?: () => void
	onSuccess?: () => void
	tripDestinations: Array<TTripDestination>
}) {
	const xhr = useXHR()
	return (
		<HotelBookingDetailsForm
			tripDestinations={tripDestinations}
			startDate={startDate}
			endDate={endDate}
			children={children}
			noOfAdults={noOfAdults}
			onCancel={onCancel}
			onSubmit={async (data) => {
				return xhr
					.post(`/hotel-bookings`, {
						details: data.hotels.map((data) => ({
							...data,
							trip_id: tripId,
						})),
						extras: data.extras.map((data) => ({
							...data,
							trip_id: tripId,
						})),
					})
					.then(onSuccess)
			}}
			tripId={tripId}
		/>
	)
}

export function AddSelfBookingDialog({
	children,
	startDate,
	endDate,
	tripId,
	tripDestinations,
	onSuccess,
}: {
	children: (props: { onCreate: () => void }) => React.ReactNode
	startDate: Date
	endDate: Date
	tripId: number
	tripDestinations: Array<TTripDestination>
	onSuccess?: () => void
}) {
	const xhr = useXHR()
	const bookingTo = useMemo(
		() => formatDate(subtractUnit(endDate, 1, "day"), "YYYY-MM-DD HH:mm:ss"),
		[endDate]
	)
	const [isOpen, show, hide] = useDialog()
	return (
		<>
			{children({ onCreate: () => show() })}
			<Dialog
				open={isOpen}
				onClose={hide}
				title="Add Self Booked Accommodation"
				sm
			>
				<Dialog.Body>
					<SelfBookForm
						bookingFrom={formatDate(startDate, "YYYY-MM-DD HH:mm:ss")}
						bookingTo={bookingTo}
						tripDestinations={tripDestinations}
						onCancel={hide}
						onSubmit={async (data) => {
							return xhr
								.post("self-booked-hotel", {
									trip_id: tripId,
									details: data.details.flatMap((h) =>
										h.dates.map((date) => ({
											name: h.hotel?.name,
											remarks: h.remarks ?? "",
											date: dateToUTCString(date),
											date_local: formatDate(date, "YYYY-MM-DD"),
											hotel_id: h.hotel?.address ? h.hotel?.id : null, // checking address to set id bcz giving new hotel name does not create new hotel it will just string and the id generated from frontend will not exist in db
										}))
									),
								})
								.then(() => {
									onSuccess?.()
									hide()
								})
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

export function EditSelfBookedDialog({
	children,
	startDate,
	endDate,
	tripDestinations,
	onSuccess,
	booking,
}: {
	children: (props: { onEdit: () => void }) => React.ReactNode
	startDate: Date
	endDate: Date
	tripDestinations: Array<TTripDestination>
	onSuccess?: () => void
	booking: TSelfBookedHotels
}) {
	const xhr = useXHR()
	const bookingTo = useMemo(
		() => formatDate(subtractUnit(endDate, 1, "day"), "YYYY-MM-DD HH:mm:ss"),
		[endDate]
	)
	const [isOpen, show, hide] = useDialog()

	const dates = booking.checkin_dates
	const selfBookingInitialValues: SelfBookFormValues = {
		details: [
			{
				dates: dates,
				hotel: booking.hotel ?? {
					booking_id: booking.name,
					name: booking.name,
				},
				remarks: booking.remarks || "",
			},
		],
	}

	return (
		<>
			{children({ onEdit: () => show() })}
			<Dialog
				open={isOpen}
				onClose={hide}
				title="Edit Self Booked Accommodation"
				sm
			>
				<Dialog.Body>
					<SelfBookForm
						propInitialValues={selfBookingInitialValues}
						bookingFrom={formatDate(startDate, "YYYY-MM-DD HH:mm:ss")}
						bookingTo={bookingTo}
						tripDestinations={tripDestinations}
						showAddMoreButton={false}
						onCancel={hide}
						onSubmit={async (data) => {
							return xhr
								.patch(`self-booked-hotel/${booking.id}`, {
									details: data.details.flatMap((h) =>
										h.dates.map((date) => ({
											name: h.hotel?.name,
											remarks: h.remarks ?? "",
											date: dateToUTCString(date),
											date_local: formatDate(date, "YYYY-MM-DD"),
											hotel_id: h.hotel?.address ? h.hotel?.id : null, // checking address to set id bcz giving new hotel name does not create new hotel it will just string and the id generated from frontend will not exist in db
										}))
									),
								})
								.then(() => {
									onSuccess?.()
									hide()
								})
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
